import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { documentsAPI } from '../services/api';
import { dateFr } from '../services/dates';
import StageModale from './StageModale';

/**
 * LA CONSERVATION DES DOCUMENTS, CÔTÉ ÉCRAN (lot D.1b) — ce que plusieurs écrans partagent
 * ======================================================================================
 * Règle : doc/specs_techniques/documents.md §5, §6 · Maquettes validées le 2026-10-07 :
 * bandeau de `admin_maquette.html`, bloc « Conservation des documents » de `suivi_de_stage_maquette.html`.
 *
 *   · `BandeauConservation` — le SUPER-ADMINISTRATEUR seul, sur tous les écrans : « N documents ont atteint
 *     leur durée de conservation (X Go) » ; « Masquer pour aujourd'hui ». Pas de courriel (DC4).
 *   · `BlocConservation` — dans le panneau d'un dossier (Stage, Certification) : l'échéance de ses documents,
 *     et « Mettre en conservation » / « Lever la conservation » (administrateur du département, motif obligatoire).
 *   · `MentionPurge` — ce qu'un document purgé affiche à la place de « Voir ».
 */

const octets = (n) => {
    const v = Number(n) || 0;
    if (v >= 1024 ** 3) return `${(v / 1024 ** 3).toFixed(1).replace('.', ',')} Go`;
    if (v >= 1024 ** 2) return `${Math.round(v / 1024 ** 2)} Mo`;
    return `${Math.max(1, Math.round(v / 1024))} Ko`;
};
const jourFr = (v) => (v ? new Date(v).toLocaleDateString('fr-FR', { timeZone: 'Europe/Paris' }) : '');
const CLE_MASQUE = 'documents.bandeau.masque';
const aujourdhui = () => new Date().toLocaleDateString('sv-SE', { timeZone: 'Europe/Paris' });

/** Le bandeau du super-administrateur (DC4). Ne se montre à personne d'autre — le serveur le dit aussi. */
export function BandeauConservation({ user }) {
    const [etat, setEtat] = useState(null);
    const [masque, setMasque] = useState(() => { try { return localStorage.getItem(CLE_MASQUE) === aujourdhui(); } catch (_) { return false; } });
    const navigate = useNavigate();
    const location = useLocation();
    const superAdmin = user?.is_super_admin === true;

    const lire = useCallback(async () => {
        if (!superAdmin) return;
        try { setEtat((await documentsAPI.echeance()).data); } catch (_) { /* un bandeau n'empêche jamais l'écran */ }
    }, [superAdmin]);
    useEffect(() => { lire(); }, [lire]);
    // Une purge ou un réglage dans Administration → Documents : le bandeau se relit.
    useEffect(() => {
        window.addEventListener('documents:echeance', lire);
        return () => window.removeEventListener('documents:echeance', lire);
    }, [lire]);

    if (!superAdmin || masque || !etat?.visible || !etat.nb) return null;
    const ouvrir = () => {
        if (location.pathname === '/admin') window.dispatchEvent(new CustomEvent('admin:onglet', { detail: 'docs' }));
        else navigate('/admin?onglet=docs');
    };
    const masquer = () => { try { localStorage.setItem(CLE_MASQUE, aujourdhui()); } catch (_) { /* rien */ } setMasque(true); };
    return (
        <div className="bandeau-conservation" role="status" style={{
            display: 'flex', alignItems: 'center', gap: 10, padding: '8px 16px', fontSize: 13,
            background: '#fffbeb', color: '#92400e', borderBottom: '1px solid #fcd34d', flexShrink: 0
        }}>
            <i className="fa-solid fa-box-archive" style={{ color: '#d97706', fontSize: 15 }} />
            <span style={{ flex: 1 }}>
                <b>{etat.nb.toLocaleString('fr-FR')} document{etat.nb > 1 ? 's ont' : ' a'} atteint {etat.nb > 1 ? 'leur' : 'sa'} durée de conservation ({octets(etat.octets)}).</b>{' '}
                La purge se fait à la main, après un récapitulatif —{' '}
                <button type="button" onClick={ouvrir} style={{ background: 'none', border: 'none', padding: 0, color: '#92400e', fontWeight: 700, textDecoration: 'underline', cursor: 'pointer', font: 'inherit' }}>
                    Administration → Documents
                </button>
            </span>
            <button type="button" onClick={masquer} style={{ background: 'none', border: '1px solid #fcd34d', color: '#92400e', borderRadius: 6, padding: '3px 9px', fontSize: 11.5, cursor: 'pointer' }}>
                Masquer pour aujourd'hui
            </button>
        </div>
    );
}

/** Ce qu'un document purgé montre à la place de « Voir » (la preuve reste). */
export function MentionPurge({ purgeLe }) {
    return (
        <span className="mention-purge" title="Le contenu a été effacé à la fin de la durée de conservation ; le nom, la taille et l'empreinte restent.">
            <i className="fa-solid fa-box-archive" /> Purgé le {jourFr(purgeLe)}
        </span>
    );
}

/** Le bloc d'un panneau de dossier (DC5). `objetTable` : 'suivi_stage' | 'certif_dossiers'. */
export function BlocConservation({ departementId, objetTable, objetId, onChange }) {
    const [etat, setEtat] = useState(null);
    const [modale, setModale] = useState(false);
    const [motif, setMotif] = useState('');
    const [erreur, setErreur] = useState(null);
    const [enCours, setEnCours] = useState(false);

    const lire = useCallback(async () => {
        if (!objetId) return;
        try { setEtat((await documentsAPI.conservation(departementId, objetTable, objetId)).data); } catch (_) { setEtat(null); }
    }, [departementId, objetTable, objetId]);
    useEffect(() => { lire(); }, [lire]);
    if (!etat) return null;

    const actif = !!etat.conservation;
    const valider = async () => {
        if (!motif.trim()) { setErreur('Le motif est obligatoire.'); return; }
        setEnCours(true); setErreur(null);
        try {
            const r = await documentsAPI.changerConservation(departementId, objetTable, objetId, motif.trim(), actif);
            setEtat(r.data); setModale(false); setMotif('');
            if (onChange) onChange();
        } catch (e) { setErreur(e.response?.data?.error || "L'enregistrement a échoué."); }
        finally { setEnCours(false); }
    };
    const echeances = etat.familles.map((f) => (f.duree === null
        ? <React.Fragment key={f.cle}> Famille « {f.libelle} » : <b>pas de purge</b> (en attente de la durée des archives).</React.Fragment>
        : <React.Fragment key={f.cle}> Famille « {f.libelle} » : <b>purgeables à partir du {dateFr(f.echeance)}</b>.</React.Fragment>));

    return (
        <div className="detail-section cons-section">
            <div className="detail-section-title"><span><i className="fa-solid fa-shield-halved" /> Conservation des documents</span></div>
            <div className={'cons-bloc' + (actif ? ' actif' : '')}>
                <div className="cons-texte">
                    {actif ? (
                        <>
                            <b><i className="fa-solid fa-shield-halved" /> En conservation</b> depuis le {jourFr(etat.conservation.pose_le)} — posée par {etat.conservation.pose_par_nom} — motif : « {etat.conservation.motif} ».<br />
                            La purge de ses documents est <b>bloquée</b> tant que la conservation n'est pas levée.
                        </>
                    ) : (
                        <>
                            {etat.derniere_levee && <>Conservation <b>levée</b> le {jourFr(etat.derniere_levee.leve_le)} par {etat.derniere_levee.leve_par_nom} — motif : « {etat.derniere_levee.motif_levee} ».<br /></>}
                            Les documents de ce dossier{etat.annee ? ` (${etat.annee})` : ''} — durée réglée par le super-administrateur :{echeances}
                        </>
                    )}
                    {etat.documents_purges > 0 && <><br /><i className="fa-solid fa-box-archive" /> {etat.documents_purges} document{etat.documents_purges > 1 ? 's' : ''} déjà purgé{etat.documents_purges > 1 ? 's' : ''} : la preuve reste.</>}
                    {!etat.peut_modifier && <><br /><i className="fa-solid fa-lock" /> {actif ? 'Lever' : 'Mettre en'} conservation : réservé à un administrateur du département.</>}
                </div>
                {etat.peut_modifier && (
                    <button type="button" className="btn btn-outline btn-sm" onClick={() => { setErreur(null); setMotif(''); setModale(true); }}>
                        <i className={'fa-solid ' + (actif ? 'fa-unlock' : 'fa-shield-halved')} /> {actif ? 'Lever la conservation' : 'Mettre en conservation'}
                    </button>
                )}
            </div>
            {modale && (
                <div onClick={(e) => e.stopPropagation()}>
                    <StageModale titre={actif ? 'Lever la conservation' : 'Mettre en conservation'} icone="fa-shield-halved" enCours={enCours}
                                 libelleValider={actif ? 'Lever la conservation' : 'Mettre en conservation'} onValider={valider}
                                 onFermer={() => { if (!enCours) setModale(false); }}>
                        <p style={{ marginBottom: 10 }}>
                            {actif
                                ? <>Les documents de ce dossier redeviennent <b>purgeables à leur échéance</b>. Si elle est déjà passée, ils apparaîtront dans le prochain récapitulatif de purge du super-administrateur.</>
                                : <>Tant que la conservation n'est pas levée, <b>aucun document de ce dossier ne sera purgé</b>, même après sa durée de conservation. À utiliser pour une procédure disciplinaire, un recours, un contentieux.</>}
                        </p>
                        <label htmlFor="cons-motif" style={{ fontSize: 11, fontWeight: 600, color: '#64748b' }}>Motif <span style={{ color: '#dc2626' }}>*</span></label>
                        <textarea id="cons-motif" className="input-sm" style={{ width: '100%', minHeight: 56 }} maxLength={1000} value={motif}
                                  onChange={(e) => { setMotif(e.target.value); setErreur(null); }}
                                  placeholder={actif ? 'Ex : procédure close' : "Ex : procédure disciplinaire en cours (rapport signalé par l'entreprise)"} />
                        {erreur && <div style={{ fontSize: 11, color: '#b91c1c', marginTop: 4 }}><i className="fa-solid fa-circle-exclamation" /> {erreur}</div>}
                        <div style={{ fontSize: 10, color: '#64748b', marginTop: 6 }}><i className="fa-solid fa-lock" /> Le motif, l'auteur et la date sont gardés dans l'historique du dossier.</div>
                    </StageModale>
                </div>
            )}
        </div>
    );
}
